import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Form } from 'informed';
import type { FormState } from 'informed';
import { useFormApi, useFormState } from 'informed';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Checkbox } from '@calefy-inc/informedMaterial';
import { useAuth } from '@calefy-inc/authentication';
import LoadingScreen from '../../../components/LoadingScreen';
import { DisplayInlineError } from '../../../../common/ErrorHandling';
import { CreateNewAdvisorButton } from './CreateNewAdvisorButton';
import { isNotNullish } from '../../../../../util';
import { ArrayElement } from '@calefy-inc/utilityTypes';
import { graphql } from '../../../../../gql';
import type { PolicystreamAdvisorsMpQuery } from '../../../../../gql/graphql';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import {
  POLICYSTREAM_INSURANCE_COMPANIES,
  BackendInsuranceCompany,
} from '../../../../AdminPanel/PolicyStream/PolicyStreamInsuranceCompanies';
import {
  withFormStyles,
  WithFormStyles,
} from '../../../../../util/withFormStyles';
import { WhiteLabelSectionRoot } from '../WhiteLabelSettings/sections/whiteLabelSectionCommon';
import { cloneFormResetBaseline } from '../WhiteLabelSettings/whiteLabelSettingsFormHelpers';
import { AdvisorProfileFormFields } from './AdvisorProfileFormFields';
import { errorify, safelyGetValues } from '../../../../../util';

import Bugsnag from '@bugsnag/browser';

const POLICYSTREAM_ADVISORS = graphql(`
  query policystreamAdvisorsMP($token: String!) {
    policystreamAdvisors(token: $token) {
      id
      organization {
        id
        name
      }
      name
      jurisdiction
      securitiesDealerName
      mortgageDealerName
      designationType
      insurers {
        id
        displayName
      }
    }
  }
`);

const UPDATE_POLICYSTREAM_ADVISOR = graphql(`
  mutation updatePolicystreamAdvisor(
    $token: String!
    $advisorId: ID!
    $name: String!
    $organization: ID!
    $jurisdiction: String
    $securitiesDealerName: String
    $mortgageDealerName: String
    $designationType: String
  ) {
    updatePolicystreamAdvisor(
      token: $token
      advisorId: $advisorId
      name: $name
      organization: $organization
      jurisdiction: $jurisdiction
      securitiesDealerName: $securitiesDealerName
      mortgageDealerName: $mortgageDealerName
      designationType: $designationType
    ) {
      advisor {
        id
        name
        organization {
          id
          name
        }
        jurisdiction
        securitiesDealerName
        mortgageDealerName
        designationType
        insurers {
          id
          displayName
        }
      }
    }
  }
`);

const UPDATE_POLICYSTREAM_ADVISOR_INSURANCE_COMPANIES = graphql(`
  mutation updatePolicystreamAdvisorInsuranceCompaniesMp(
    $token: String!
    $advisorId: ID!
    $changes: [AdvisorInsurerChangeInput!]!
  ) {
    updatePolicystreamAdvisorInsuranceCompanies(
      token: $token
      advisorId: $advisorId
      changes: $changes
    ) {
      advisor {
        id
        insurers {
          id
          displayName
        }
      }
    }
  }
`);

// Derive the Advisor shape from the generated query type.
type Advisor = NonNullable<
  ArrayElement<PolicystreamAdvisorsMpQuery['policystreamAdvisors']>
>;

function adviserIncludeInsurerFieldName(insurerId: string): string {
  return `includeInsurer_${insurerId}`;
}

/** Checkbox values from Informed may be `undefined` if unset; treat as false. */
function readFormBoolean(value: unknown): boolean {
  if (value === true) {
    return true;
  }
  if (value === 'true' || value === 1) {
    return true;
  }
  return false;
}

/**
 * Informed baseline: only linked insurers get explicit `true`; other checkbox
 * fields stay unset (read as false). The master insurers list is still loaded
 * separately to render every option and to diff on save.
 */
function buildAdvisorFormInitialValues(
  advisor: Advisor,
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    name: advisor.name,
    jurisdiction: advisor.jurisdiction,
    securitiesDealerName: advisor.securitiesDealerName ?? '',
    mortgageDealerName: advisor.mortgageDealerName ?? '',
    designationType: advisor.designationType ?? '',
  };
  for (const row of advisor.insurers) {
    if (row == null) continue;
    base[adviserIncludeInsurerFieldName(row.id)] = true;
  }
  return base;
}

/** Save / reset — must render inside `<Form>` (Informed context). */
function AdvisorFormSaveBar({
  disabled,
  resetBaseline,
}: {
  disabled: boolean;
  resetBaseline: Record<string, unknown>;
}) {
  const formApi = useFormApi();
  const { dirty } = useFormState();

  const handleReset = useCallback(() => {
    formApi.reset({ values: cloneFormResetBaseline(resetBaseline) });
  }, [formApi, resetBaseline]);

  return (
    <SaveSubmitRow>
      <ResetButton
        type='button'
        variant='outlined'
        disabled={disabled || !dirty}
        onClick={handleReset}
      >
        Reset
      </ResetButton>
      <SubmitButton type='submit' variant='contained' disabled={disabled}>
        {disabled ? 'Saving…' : 'Save'}
      </SubmitButton>
    </SaveSubmitRow>
  );
}

const PolicyStreamAdvisorsMPBase = ({ classes }: WithFormStyles) => {
  const { token } = useAuth();
  const [getAdvisors, { data, loading, error }] = useLazyQuery(
    POLICYSTREAM_ADVISORS,
  );
  const [
    getInsurers,
    { data: insurerData, loading: insurersLoading, called: insurersCalled },
  ] = useLazyQuery(POLICYSTREAM_INSURANCE_COMPANIES);
  const [advisors, setAdvisors] = useState<Array<Advisor>>([]);

  useEffect(() => {
    if (token) {
      getAdvisors({ variables: { token } });
      getInsurers({ variables: { token } });
    }
  }, [token]);

  useEffect(() => {
    if (data?.policystreamAdvisors) {
      setAdvisors(data.policystreamAdvisors.filter(isNotNullish));
    }
  }, [data]);

  const insurers = (insurerData?.policystreamInsuranceCompanies || []).filter(
    isNotNullish,
  );

  // Insurer list must be settled before mounting advisor forms (checkbox baseline + grid).
  const advisorFormReady = !token || (insurersCalled && !insurersLoading);

  return (
    <>
      {loading ? <LoadingScreen /> : null}
      <ErrorArea sx={loading ? { mb: 0 } : undefined}>
        <DisplayInlineError error={error} />
      </ErrorArea>

      <WhiteLabelSectionRoot>
        <SectionHeaderRow>
          <Typography variant='h6' gutterBottom>
            {advisors.length === 1 ? 'Advisor' : 'Advisors'}
          </Typography>
          <SectionHeaderActions>
            <CreateNewAdvisorButton />
          </SectionHeaderActions>
        </SectionHeaderRow>
        {advisors.length > 0 ? (
          !advisorFormReady ? (
            <AdvisorFormLoadingCenter>
              <CircularProgress />
            </AdvisorFormLoadingCenter>
          ) : (
            <Stack spacing={1}>
              {advisors.map((advisor) => (
                <DisplayAdvisor
                  key={advisor.id}
                  advisor={advisor}
                  insurers={insurers}
                  refetchAdvisors={() => {
                    if (token) {
                      void getAdvisors({ variables: { token } });
                    }
                  }}
                  classes={classes}
                />
              ))}
            </Stack>
          )
        ) : (
          <NoAdvisorsComponent />
        )}
      </WhiteLabelSectionRoot>
    </>
  );
};

export const PolicyStreamAdvisorsMP = withFormStyles(() => ({
  form: {
    margin: 0,
  },
}))(PolicyStreamAdvisorsMPBase);

interface DisplayAdvisorProps extends WithFormStyles {
  advisor: Advisor;
  insurers: Array<BackendInsuranceCompany>;
  refetchAdvisors: () => void;
}
const DisplayAdvisor = ({
  advisor,
  insurers,
  refetchAdvisors,
  classes,
}: DisplayAdvisorProps) => {
  const { token } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [updateAdvisor, { loading: savingAdvisor }] = useMutation(
    UPDATE_POLICYSTREAM_ADVISOR,
    {
      errorPolicy: 'all',
    },
  );
  const [updateAdvisorInsurer, { loading: savingInsurerLinks }] = useMutation(
    UPDATE_POLICYSTREAM_ADVISOR_INSURANCE_COMPANIES,
    {
      errorPolicy: 'all',
    },
  );

  // Either mutation in flight — may briefly clear between sequential awaits (reviewer preference vs local state).
  const saveInFlight = savingAdvisor || savingInsurerLinks;

  const initialValues = buildAdvisorFormInitialValues(advisor);

  const formResetKey = [
    advisor.id,
    advisor.name,
    advisor.organization.id,
    advisor.organization.name,
    advisor.jurisdiction,
    advisor.securitiesDealerName ?? '',
    advisor.mortgageDealerName ?? '',
    advisor.designationType ?? '',
    advisor.insurers
      .map((row: Advisor['insurers'][number]) => row.id)
      .sort()
      .join(','),
    insurers
      .map((ic: BackendInsuranceCompany) => ic.id)
      .sort()
      .join(','),
  ].join('-');

  const handleFormSubmit = async (formState: FormState) => {
    if (!token) {
      enqueueSnackbar('No auth token — please log in again', {
        variant: 'error',
      });
      return;
    }
    const {
      name: nameRaw,
      jurisdiction,
      securitiesDealerName,
      mortgageDealerName,
      designationType,
    } = safelyGetValues(
      formState.values,
      [
        'name',
        'jurisdiction',
        'securitiesDealerName',
        'mortgageDealerName',
        'designationType',
      ],
      (v) => v?.trim() || undefined,
    );

    const name = nameRaw || '';
    // Organization is not editable from this panel — keep the advisor's
    // existing organization so the required mutation arg is still provided.
    const organization = advisor.organization.id;

    const baselineIncluded = (insurerId: string) =>
      advisor.insurers.some(
        (row: Advisor['insurers'][number]) => row.id === insurerId,
      );
    const insurerLinkChanges: Array<{
      insurerId: string;
      include: boolean;
    }> = [];
    for (const ins of insurers) {
      const field = adviserIncludeInsurerFieldName(ins.id);
      const next = readFormBoolean(formState.values[field]);
      if (next !== baselineIncluded(ins.id)) {
        insurerLinkChanges.push({ insurerId: ins.id, include: next });
      }
    }

    try {
      const advisorRes = await updateAdvisor({
        variables: {
          token,
          advisorId: advisor.id,
          name,
          organization,
          jurisdiction,
          securitiesDealerName: securitiesDealerName || null,
          mortgageDealerName: mortgageDealerName || null,
          designationType: designationType || null,
        },
      });
      if (advisorRes.data?.updatePolicystreamAdvisor == null) {
        enqueueSnackbar('Failed to save advisor', { variant: 'error' });
        return;
      }
      if (insurerLinkChanges.length > 0) {
        const relRes = await updateAdvisorInsurer({
          variables: {
            token,
            advisorId: advisor.id,
            changes: insurerLinkChanges,
          },
        });
        if (relRes.data?.updatePolicystreamAdvisorInsuranceCompanies == null) {
          enqueueSnackbar('Failed to update insurer link', {
            variant: 'error',
          });
          refetchAdvisors();
          return;
        }
      }
      enqueueSnackbar('Advisor saved successfully', { variant: 'success' });
      refetchAdvisors();
    } catch (err) {
      const error = errorify(err);
      enqueueSnackbar(`Failed to save: ${error.message}`, {
        variant: 'error',
      });
      Bugsnag.notify(error);
    }
  };

  const sameAdvisorAndOrgName = advisor.name === advisor.organization.name;

  return (
    <AdvisorCard>
      <AdvisorCardHeader>
        <Typography variant='subtitle1' fontWeight={600}>
          {sameAdvisorAndOrgName
            ? advisor.name
            : `${advisor.name} - ${advisor.organization.name}`}
        </Typography>
      </AdvisorCardHeader>
      <AdvisorCardBody>
        <Stack spacing={1}>
          <Form
            key={formResetKey}
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
          >
            <AdvisorFieldsGrid>
              <AdvisorProfileFormFields
                layout='card'
                textfieldClassName={classes.textfield}
                nameFieldDescription="Update the advisor's name."
              />
            </AdvisorFieldsGrid>
            {insurers.length > 0 ? (
              <Box>
                <InsurersSectionTitle>Insurers</InsurersSectionTitle>
                <InsurersFieldsGrid>
                  {insurers.map((insurer) => (
                    <InsurerCard key={insurer.id}>
                      <InsurerFormControlLabel
                        control={
                          <Checkbox
                            name={adviserIncludeInsurerFieldName(insurer.id)}
                          />
                        }
                        label={
                          <InsurerCheckboxLabel variant='body2'>
                            {insurer.displayName}
                          </InsurerCheckboxLabel>
                        }
                      />
                    </InsurerCard>
                  ))}
                </InsurersFieldsGrid>
              </Box>
            ) : null}
            <AdvisorFormSaveBar
              disabled={saveInFlight}
              resetBaseline={initialValues}
            />
          </Form>
        </Stack>
      </AdvisorCardBody>
    </AdvisorCard>
  );
};

const NoAdvisorsComponent = () => {
  return (
    <NoAdvisorsBox>
      <Typography variant='body2' color='text.secondary'>
        No advisors found. Use &quot;Add new advisor&quot; to create one.
      </Typography>
    </NoAdvisorsBox>
  );
};

/** Save / reset actions (aligned with CompanyAccountForm). */
const SaveSubmitRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  gap: theme.spacing(2),
}));

const ResetButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(14),
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  minWidth: theme.spacing(14),
}));

/** Space above inline errors; collapses when the page is in loading state */
const ErrorArea = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

/** Section title row: stacks on small screens, horizontal on sm+ (matches White Label section headers) */
const SectionHeaderRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}));

const SectionHeaderActions = styled(Box)(() => ({
  flexShrink: 0,
}));

const AdvisorCard = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const AdvisorCardHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}));

const AdvisorCardBody = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 2.5, 2),
  backgroundColor: theme.palette.background.paper,
}));

/** Spinner for the advisors section while the insurer catalog is still loading. */
const AdvisorFormLoadingCenter = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(6, 0),
}));

/** Same responsive two-column grid pattern as CompanyInfoSection FieldsGrid */
const AdvisorFieldsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(2),
  gridTemplateColumns: '1fr',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const InsurersFieldsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: theme.spacing(1.5),
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const InsurerCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(0, 1.5),
  backgroundColor: theme.palette.background.paper,
}));

const InsurersSectionTitle = styled('h4')(({ theme }) => ({
  margin: theme.spacing(1.5, 0),
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
}));

const InsurerFormControlLabel = styled(FormControlLabel)(() => ({
  margin: 0,
  alignItems: 'center',
  width: '100%',
}));

const InsurerCheckboxLabel = styled(Typography)(() => ({
  lineHeight: 1.4,
  paddingTop: 0,
  paddingBottom: 0,
}));

const NoAdvisorsBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 2),
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor:
    theme.palette.mode === 'light'
      ? theme.palette.action.hover
      : theme.palette.action.selected,
}));
